import React from 'react';
import loadable from '@loadable/component';

import { bool, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { camelize } from '../../util/string';
import { propTypes } from '../../util/types';

import FallbackPage from './FallbackPage';
import { ASSET_NAME } from './LandingPage.duck';
import SectionMembershipBanner from '../PageBuilder/SectionBuilder/SectionMembershipBanner/SectionMembershipBanner';

const PageBuilder = loadable(() =>
  import(/* webpackChunkName: "PageBuilder" */ '../PageBuilder/PageBuilder')
);

// Synthetic section the operator can't create in Console — we splice it into
// the fetched landing-page sections so it renders above the "Our Picks"
// section using the normal SectionBuilder pipeline (matching widths,
// spacing, theming).
const MEMBERSHIP_SECTION = {
  sectionId: 'membership-banner',
  sectionType: 'membershipBanner',
};

// Heuristic that finds the "Our Picks" section in the operator-managed
// landing-page asset. We match on either the section's id or the heading
// title content. Falls back to index 0 if no Our Picks section exists.
const findOurPicksIndex = sections => {
  if (!Array.isArray(sections)) return 0;
  const idx = sections.findIndex(s => {
    const idMatch = typeof s?.sectionId === 'string' && /our[-_ ]?picks/i.test(s.sectionId);
    const title = s?.title?.content || s?.title?.text || '';
    const titleMatch = typeof title === 'string' && /our\s+picks/i.test(title);
    return idMatch || titleMatch;
  });
  return idx >= 0 ? idx : 0;
};

const withMembershipSection = pageData => {
  if (!pageData) return pageData;
  const sections = Array.isArray(pageData.sections) ? pageData.sections : [];
  // Idempotent: don't double-insert if the operator (or a re-render) already
  // added the synthetic section.
  if (sections.some(s => s?.sectionType === 'membershipBanner')) return pageData;
  const insertAt = findOurPicksIndex(sections);
  const next = [...sections];
  next.splice(insertAt, 0, MEMBERSHIP_SECTION);
  return { ...pageData, sections: next };
};

const customSectionComponents = {
  membershipBanner: { component: SectionMembershipBanner },
};

export const LandingPageComponent = props => {
  const { pageAssetsData, inProgress, error } = props;
  const rawPageData = pageAssetsData?.[camelize(ASSET_NAME)]?.data;
  const pageData = withMembershipSection(rawPageData);

  return (
    <PageBuilder
      pageAssetsData={pageData}
      inProgress={inProgress}
      error={error}
      fallbackPage={<FallbackPage error={error} />}
      options={{ sectionComponents: customSectionComponents }}
    />
  );
};

LandingPageComponent.propTypes = {
  pageAssetsData: object,
  inProgress: bool,
  error: propTypes.error,
};

const mapStateToProps = state => {
  const { pageAssetsData, inProgress, error } = state.hostedAssets || {};
  return { pageAssetsData, inProgress, error };
};

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const LandingPage = compose(connect(mapStateToProps))(LandingPageComponent);

export default LandingPage;
